import axiosConfig from "../Service/axiosConfig.js";
import { store } from "../redux/Store.js";
import { flushCartPendingMutations } from "../redux/Slices/cartSlice.js";

/**
 * Centralized Checkout & Payment Navigation
 * Extracts, validates, and initiates the draft order transition to /checkout.
 */
export const executePaymentNavigate = async ({
  Order,
  cart,
  orderAddress,
  navigate,
  setErrorMessage,
  setSmShow,
  setErrorMsg,
  setNoStock,
}) => {
  // Flush any pending debounced cart mutations so server DB has final quantities
  try {
    await flushCartPendingMutations(store.dispatch, store.getState);
  } catch (e) {
    console.warn("Failed to flush pending cart mutations before checkout:", e);
  }

  const latestCart = store.getState().cart;

  const normalizedOrder = {
    ...Order,
    stores: Number(Order?.stores || localStorage.getItem("storeId") || 0),
    user_id: Number(localStorage.getItem("userId") || Order?.user_id || 0),
    address: Number(
      Order?.address || localStorage.getItem("address_id") || orderAddress?.id || 0
    ),
    products:
      Order?.products && Order.products.length > 0
        ? Order.products
        : (latestCart?.products || cart?.products || []).map((item) => ({
            id: item.id,
            min_qty: item.quantity,
          })),
  };

  if (localStorage.getItem("oa_delivery_charges") !== null) {
    normalizedOrder.delivery_charges = parseFloat(
      localStorage.getItem("oa_delivery_charges")
    );
  }
  if (localStorage.getItem("oa_packing_charges") !== null) {
    normalizedOrder.packing_charges = parseFloat(
      localStorage.getItem("oa_packing_charges")
    );
  }

  if (
    !normalizedOrder.stores ||
    !normalizedOrder.user_id ||
    !normalizedOrder.address
  ) {
    const missing = [];
    if (!normalizedOrder.stores) missing.push("store");
    if (!normalizedOrder.user_id) missing.push("user");
    if (!normalizedOrder.address) missing.push("address");
    const msg = `Missing required order data: ${missing.join(", ")}`;
    if (setErrorMessage) {
      setErrorMessage(`${Date.now()}-${msg}`);
    } else if (setErrorMsg) {
      setErrorMsg(msg);
      if (setSmShow) setSmShow(true);
    }
    return;
  }

  let order_obj = normalizedOrder;
  let request_type = "post";
  let url = "/accounts/orders/";
  if (localStorage.getItem("orderId")) {
    order_obj["order_id"] = localStorage.getItem("orderId");
    request_type = "put";
    url = `/accounts/orders/${localStorage.getItem("orderId")}/`;
  }

  try {
    axiosConfig.defaults.headers["Content-Type"] = "application/json";
    const res = await axiosConfig[request_type](url, order_obj);
    localStorage.setItem("orderId", res.data.id);
    if (normalizedOrder.address) {
      localStorage.setItem("address_id", normalizedOrder.address);
    }
    if (navigate) {
      navigate("/checkout");
    }
    return res.data;
  } catch (error) {
    console.error("Order create/update failed", error?.response?.data || error);

    const errData = error.response?.data;
    const errObj = errData?.error;
    let formattedMsg = "Failed to proceed to checkout";

    if (errObj && errObj.code === "PRODUCT_NOT_AVAILABLE_IN_STORE") {
      const productNames = Array.isArray(errObj.products)
        ? errObj.products.map((p) => p.product_name).filter(Boolean).join(", ")
        : "";
      const baseMsg =
        errObj.message ||
        "Some products in your cart are not available at the selected store.";
      formattedMsg = productNames
        ? `${baseMsg} Unavailable item(s): ${productNames}. Please remove them or choose a different address.`
        : `${baseMsg} Please check your cart or choose a different address.`;
    } else if (typeof errObj === "string") {
      formattedMsg = errObj;
    } else if (errObj?.message) {
      formattedMsg = errObj.message;
    } else if (errData?.message) {
      formattedMsg = errData.message;
    }

    if (setErrorMsg) setErrorMsg(formattedMsg);
    if (setSmShow) setSmShow(true);
    if (setErrorMessage) setErrorMessage(`${Date.now()}-${formattedMsg}`);

    // Support both new and legacy unavailable products structures
    if (
      errObj &&
      errObj.code === "PRODUCT_NOT_AVAILABLE_IN_STORE" &&
      Array.isArray(errObj.products) &&
      setNoStock
    ) {
      setNoStock(
        errObj.products.map((p) => ({
          id: p.product_id,
          name: p.product_name,
        }))
      );
    } else if (
      error.response?.data &&
      error.response.data["The following products are not available in the store:"] &&
      setNoStock
    ) {
      const unavailable =
        error.response.data["The following products are not available in the store:"];
      unavailable.forEach(async (res) => {
        try {
          const productRes = await axiosConfig.get(
            `/catlog/products/${res.id}/?is_suspended=false`
          );
          setNoStock((prestate) => [...prestate, productRes.data]);
          if (setSmShow) setSmShow(true);
        } catch (e) {
          console.error(e);
        }
      });
    }
    return null;
  }
};

export default executePaymentNavigate;
